import { apiRequest, apiRequestBlob } from '@/lib/httpClient'
import type {
  BulkUpdateAssetReviewRequest,
  BulkUpdateAssetReviewResponse,
  ComplianceAssetReviewItem,
  ComplianceJobListItem,
  ComplianceJobsResponse,
  ComplianceJobStatusResponse,
  ComplianceLogItem,
  ComplianceLogsResponse,
  CompleteComplianceUploadSessionRequest,
  CreateComplianceUploadSessionRequest,
  CreateComplianceUploadSessionResponse,
  CreateComplianceJobResponse,
  ReanalyzeJobResponse,
  TrialBalancePlatform,
  UpdateAssetReviewRequest,
  UpdateAssetReviewResponse,
  UploadJobFilesResponse,
  XeroTrialBalanceRow,
} from '@/features/compliance/types'

export async function fetchTrialBalanceAvailability(clientId: string): Promise<TrialBalancePlatform[]> {
  const data = await apiRequest<TrialBalancePlatform[]>('Compliance/trial-balance-availability', {
    method: 'GET',
    query: { clientId },
  })
  return Array.isArray(data) ? data : []
}

export async function fetchComplianceJobs(
  clientId: string,
  skip: number,
  take: number,
  orgId: string
): Promise<ComplianceJobsResponse> {
  const payload = await apiRequest<{ items: ComplianceJobListItem[]; total: number }>(
    'Compliance/jobs',
    {
      method: 'GET',
      query: { clientId, skip, take, orgId },
    }
  )
  return {
    items: Array.isArray(payload.items) ? payload.items : [],
    total: typeof payload.total === 'number' ? payload.total : 0,
  }
}

export async function fetchComplianceJobStatus(
  clientId: string,
  jobId: string
): Promise<ComplianceJobStatusResponse> {
  return apiRequest<ComplianceJobStatusResponse>(`Compliance/job/${jobId}/status`, {
    method: 'GET',
    query: { clientId },
  })
}

export async function createComplianceJob(
  clientId: string,
  periodEndDate: string,
  orgId?: string | null
): Promise<CreateComplianceJobResponse> {
  return apiRequest<CreateComplianceJobResponse>('Compliance/job', {
    method: 'POST',
    query: { clientId },
    body: { periodEndDate, orgId: orgId ?? undefined },
  })
}

export async function deleteComplianceJob(clientId: string, jobId: string): Promise<void> {
  await apiRequest<void>(`Compliance/job/${jobId}`, {
    method: 'DELETE',
    query: { clientId },
  })
}

export async function fetchXeroTrialBalance(
  clientId: string,
  jobId: string
): Promise<XeroTrialBalanceRow[]> {
  const data = await apiRequest<XeroTrialBalanceRow[]>(
    `Compliance/job/${jobId}/xero-trial-balance`,
    { method: 'GET', query: { clientId } }
  )
  return Array.isArray(data) ? data : []
}

export async function fetchComplianceAssets(
  clientId: string,
  jobId: string
): Promise<ComplianceAssetReviewItem[]> {
  const data = await apiRequest<ComplianceAssetReviewItem[]>(
    `Compliance/job/${jobId}/assets`,
    { method: 'GET', query: { clientId } }
  )
  return Array.isArray(data) ? data : []
}

export async function fetchComplianceLogs(
  clientId: string,
  jobId: string
): Promise<ComplianceLogsResponse> {
  const data = await apiRequest<{ logs?: ComplianceLogItem[]; status?: string }>(
    `Compliance/job/${jobId}/logs`,
    { method: 'GET', query: { clientId } }
  )
  return {
    logs: Array.isArray(data?.logs) ? data.logs : [],
    status: typeof data?.status === 'string' ? data.status : '',
  }
}

function getFilenameFromContentDisposition(header: Headers | undefined, fallback: string): string {
  const cd = header?.get('Content-Disposition')
  if (!cd) return fallback
  const match = /filename[^;=\n]*=(?:"([^"]*)"|([^;\n]*))/i.exec(cd)
  const name = match ? (match[1] || match[2] || '').trim() : ''
  return name || fallback
}

export async function downloadComplianceExport(
  clientId: string,
  jobId: string,
  format: 'excel' | 'pdf'
): Promise<{ blob: Blob; filename: string }> {
  const path = `Compliance/job/${jobId}/export/${format === 'excel' ? 'excel' : 'pdf'}`
  const accept = format === 'excel'
    ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    : 'application/pdf'
  const { blob, headers } = await apiRequestBlob(path, {
    method: 'GET',
    query: { clientId },
    headers: { Accept: accept },
  })
  const ext = format === 'excel' ? 'xlsx' : 'pdf'
  const filename = getFilenameFromContentDisposition(headers, `TB-Review-Report.${ext}`)
  return { blob, filename }
}

export async function uploadJobFiles(
  clientId: string,
  payload: { jobId: string; indexFile?: File | null; workpaperFiles: File[]; orgId?: string; trialBalanceSource?: string }
): Promise<UploadJobFilesResponse> {
  const session = await apiRequest<CreateComplianceUploadSessionResponse>('Compliance/upload-session', {
    method: 'POST',
    query: { clientId },
    body: {
      jobId: payload.jobId,
      orgId: payload.orgId,
      trialBalanceSource: payload.trialBalanceSource,
      indexFile: payload.indexFile
        ? {
            fileName: payload.indexFile.name,
            contentType: payload.indexFile.type || 'application/octet-stream',
            size: payload.indexFile.size,
          }
        : undefined,
      workpaperFiles: payload.workpaperFiles.map((file) => ({
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
        size: file.size,
      })),
    } satisfies CreateComplianceUploadSessionRequest,
  })

  if (payload.indexFile && session.indexFile) {
    await uploadFileToS3(session.indexFile.uploadUrl, payload.indexFile, session.indexFile.contentType)
  }

  for (const [index, file] of payload.workpaperFiles.entries()) {
    const descriptor = session.workpaperFiles[index]
    if (!descriptor) {
      throw new Error(`Missing upload target for ${file.name}`)
    }
    await uploadFileToS3(descriptor.uploadUrl, file, descriptor.contentType)
  }

  return apiRequest<UploadJobFilesResponse>(
    `Compliance/upload-session/${encodeURIComponent(session.uploadSessionId)}/complete`,
    {
      method: 'POST',
      query: { clientId },
      body: {
        jobId: payload.jobId,
        orgId: payload.orgId,
        trialBalanceSource: payload.trialBalanceSource,
        indexFile: session.indexFile ?? undefined,
        workpaperFiles: session.workpaperFiles,
      } satisfies CompleteComplianceUploadSessionRequest,
    }
  )
}

async function uploadFileToS3(uploadUrl: string, file: File, contentType: string) {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': contentType || file.type || 'application/octet-stream',
    },
    body: file,
  })

  if (!response.ok) {
    throw new Error(`Failed to upload ${file.name}`)
  }
}

export async function updateAssetReview(
  clientId: string,
  jobId: string,
  complianceAssetId: string,
  payload: UpdateAssetReviewRequest
): Promise<UpdateAssetReviewResponse> {
  return apiRequest<UpdateAssetReviewResponse>(
    `Compliance/job/${jobId}/asset/${complianceAssetId}/review`,
    {
      method: 'PUT',
      query: { clientId },
      body: payload,
    }
  )
}

export async function bulkUpdateAssetReview(
  clientId: string,
  jobId: string,
  payload: BulkUpdateAssetReviewRequest
): Promise<BulkUpdateAssetReviewResponse> {
  return apiRequest<BulkUpdateAssetReviewResponse>(
    `Compliance/job/${jobId}/assets/review/bulk`,
    {
      method: 'PUT',
      query: { clientId },
      body: payload,
    }
  )
}

export async function reanalyzeJob(
  clientId: string,
  payload: { jobId: string; workpaperFiles: File[]; orgId?: string }
): Promise<ReanalyzeJobResponse> {
  const formData = new FormData()
  formData.append('JobId', payload.jobId)
  if (payload.orgId) formData.append('OrgId', payload.orgId)
  payload.workpaperFiles.forEach((f) => formData.append('Files', f))
  return apiRequest<ReanalyzeJobResponse>(`Compliance/job/${payload.jobId}/reanalyze`, {
    method: 'POST',
    query: { clientId },
    formData,
  })
}
